/**
 * Cuts an Ogg Opus file into short standalone Ogg Opus files, without decoding.
 *
 * Web Audio decodes a whole file at once, and an hour of stereo voice decoded
 * is hundreds of megabytes: too much for a phone. A byte range of an Ogg file
 * cannot be decoded alone, though: it needs the two header pages, and its
 * pages' sequence numbers and granule positions must read as a stream that
 * starts at zero. So each piece is: the original header pages, then the audio
 * pages that cover the piece, renumbered, with their granules moved back and
 * their checksums written again (RFC 7845 over RFC 3533).
 *
 * Time on an Opus stream: a page's granule is the number of decoded samples
 * (at 48 kHz) up to its end, the pre-skip included; the decoder drops the
 * first `preSkip` samples. So sample k of the decoder is at (k − preSkip) / 48000 s.
 */

export const OPUS_RATE = 48_000;
/** Decoding starts this far before the wanted time, so the decoder has settled by then (RFC 7845 §4.6 advises 80 ms). */
const PREROLL_SAMPLES = 3840;

export interface OggPage {
  /** Where the page starts in the file. */
  offset: number;
  /** Header and body. */
  length: number;
  /** 0x01: continues a packet from the page before; 0x02: first page; 0x04: last page. */
  flags: number;
  /** −1 when no packet ends on this page. */
  granule: number;
  serial: number;
}

export interface OpusStream {
  channels: number;
  preSkip: number;
  /** The pages that hold OpusHead and OpusTags. */
  headerPages: OggPage[];
  audioPages: OggPage[];
  /** Decoder samples before each audio page (the granule of the audio before it). */
  startSamples: number[];
}

/**
 * Reads the complete pages in `bytes` from `from` on. Stops at the first page
 * not fully there yet: `next` is where to start once more bytes have come.
 * A byte that does not start a page is skipped (a damaged file still plays).
 */
export function readOggPages(bytes: Uint8Array, from = 0): { pages: OggPage[]; next: number } {
  const pages: OggPage[] = [];
  let at = from;
  while (at + 27 <= bytes.length) {
    if (!(bytes[at] === 0x4f && bytes[at + 1] === 0x67 && bytes[at + 2] === 0x67 && bytes[at + 3] === 0x53)) {
      at++;
      continue;
    }
    const segments = bytes[at + 26];
    if (at + 27 + segments > bytes.length) break;
    let body = 0;
    for (let i = 0; i < segments; i++) body += bytes[at + 27 + i];
    const length = 27 + segments + body;
    if (at + length > bytes.length) break;
    const view = new DataView(bytes.buffer, bytes.byteOffset + at, 27);
    const low = view.getUint32(6, true);
    const high = view.getInt32(10, true);
    pages.push({ offset: at, length, flags: bytes[at + 5], granule: high === -1 && low === 0xffffffff ? -1 : high * 2 ** 32 + low, serial: view.getUint32(14, true) });
    at += length;
  }
  return { pages, next: at };
}

/** The first packet of a page, when it starts on that page. */
function firstPacket(bytes: Uint8Array, page: OggPage): Uint8Array {
  const segments = bytes[page.offset + 26];
  let size = 0;
  for (let i = 0; i < segments; i++) {
    size += bytes[page.offset + 27 + i];
    if (bytes[page.offset + 27 + i] < 255) break;
  }
  const start = page.offset + 27 + segments;
  return bytes.subarray(start, start + size);
}

/**
 * Sorts the pages read so far into header and audio, once the first page has
 * shown it is Opus. Null when it is not Opus (Vorbis, FLAC in Ogg, a video).
 */
export function opusStream(bytes: Uint8Array, pages: OggPage[]): OpusStream | null {
  if (pages.length === 0) return null;
  const head = firstPacket(bytes, pages[0]);
  if (head.length < 19 || String.fromCharCode(...head.subarray(0, 8)) !== "OpusHead") return null;
  const channels = head[9];
  const preSkip = head[10] | (head[11] << 8);
  const serial = pages[0].serial;
  const own = pages.filter((p) => p.serial === serial);
  // The headers are the pages before the first one with a granule: OpusTags can span several pages.
  let firstAudio = own.findIndex((p, i) => i > 0 && p.granule > 0);
  if (firstAudio === -1) firstAudio = own.length;
  const audioPages = own.slice(firstAudio);
  const startSamples: number[] = [];
  let before = 0;
  for (const page of audioPages) {
    startSamples.push(before);
    if (page.granule >= 0) before = page.granule;
  }
  return { channels, preSkip, headerPages: own.slice(0, firstAudio), audioPages, startSamples };
}

/** The stream's length in seconds, from its last page. */
export function opusDuration(stream: OpusStream): number {
  for (let i = stream.audioPages.length - 1; i >= 0; i--) {
    const g = stream.audioPages[i].granule;
    if (g >= 0) return Math.max(0, g - stream.preSkip) / OPUS_RATE;
  }
  return 0;
}

export interface PieceRange {
  /** Audio pages [first, last] (inclusive) that make the piece. */
  first: number;
  last: number;
  /** The time, in the whole file, of the piece's first decoded sample. */
  start: number;
  /** Whether the piece reaches the last page read so far: more pages may still come. */
  atEnd: boolean;
}

/**
 * Which audio pages make the piece that covers [from, to) seconds: from a page
 * a little before `from` (and not one that continues a packet) to the first
 * page that ends at or after `to`. Null when no page covers `from` yet.
 */
export function pieceRange(stream: OpusStream, from: number, to: number): PieceRange | null {
  const { audioPages: pages, startSamples: starts, preSkip } = stream;
  if (pages.length === 0) return null;
  const want = Math.max(0, preSkip + Math.floor(from * OPUS_RATE) - PREROLL_SAMPLES);
  // The last page that starts at or before `want`.
  let lo = 0;
  let hi = pages.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= want) lo = mid;
    else hi = mid - 1;
  }
  let first = lo;
  while (first > 0 && pages[first].flags & 0x01) first--;
  const end = preSkip + Math.ceil(to * OPUS_RATE);
  let last = first;
  while (last < pages.length - 1 && !(pages[last].granule >= end)) last++;
  // Nothing past `from` yet: wait for more pages.
  const lastGranule = pages[last].granule >= 0 ? pages[last].granule : starts[last];
  if (lastGranule - preSkip <= from * OPUS_RATE && !(pages[last].flags & 0x04)) return null;
  return { first, last, start: starts[first] / OPUS_RATE, atEnd: last === pages.length - 1 };
}

/** Ogg's CRC-32: polynomial 0x04C11DB7, not reflected, starting at 0. */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let b = 0; b < 8; b++) r = r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1;
    table[i] = r >>> 0;
  }
  return table;
})();

export function oggCrc(data: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < data.length; i++) crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  return crc;
}

/** A standalone Ogg Opus file: the header pages, then the piece's pages renumbered from where the headers end. */
export function buildPiece(bytes: Uint8Array, stream: OpusStream, range: PieceRange): Uint8Array {
  const pages = [...stream.headerPages, ...stream.audioPages.slice(range.first, range.last + 1)];
  const out = new Uint8Array(pages.reduce((n, p) => n + p.length, 0));
  const shift = stream.startSamples[range.first];
  const audioFrom = stream.headerPages.length;
  let at = 0;
  pages.forEach((page, i) => {
    out.set(bytes.subarray(page.offset, page.offset + page.length), at);
    const view = new DataView(out.buffer, at, page.length);
    if (i >= audioFrom) {
      let flags = out[at + 5] & ~0x02;
      if (i === audioFrom) flags &= ~0x01;
      if (i === pages.length - 1) flags |= 0x04;
      else flags &= ~0x04;
      out[at + 5] = flags;
      if (page.granule >= 0) {
        const g = page.granule - shift;
        view.setUint32(6, g % 2 ** 32, true);
        view.setUint32(10, Math.floor(g / 2 ** 32), true);
      }
    }
    view.setUint32(18, i, true);
    view.setUint32(22, 0, true);
    view.setUint32(22, oggCrc(out.subarray(at, at + page.length)), true);
    at += page.length;
  });
  return out;
}
